import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import ZoomFloat from 'components/ZoomFloat';

/**
 * MeetingContext — una sola ventana de Zoom para toda la app (#20).
 *
 * Antes cada página (Asambleas, panel del staff, panel del propietario,
 * Resultados) montaba su propio <ZoomFloat>; al navegar a otra sección la
 * página se desmontaba y con ella el iframe → la reunión se cortaba. Ahora
 * la ventana vive aquí, por encima de las rutas, y las páginas solo piden
 * abrirla con abrirReunion(). Sigue abierta al cambiar de sección.
 *
 * alCerrar (opcional) corre al pulsar la X o salir desde Zoom: el staff la
 * usa para terminar la reunión para todos (endMeeting); el propietario no
 * la pasa y solo se cierra su ventana. Al cerrar sesión la ventana se cierra
 * sin llamar a alCerrar (el token ya no sirve).
 */
const MeetingContext = createContext(null);

export function MeetingProvider({ children }) {
  const { user } = useAuth();
  const [reunion, setReunion] = useState(null);
  const reunionRef = useRef(null);
  reunionRef.current = reunion;

  const abrirReunion = useCallback(({ assemblyId, assemblyName, isModerator = false, alCerrar }) => {
    setReunion({ assemblyId, assemblyName, isModerator, alCerrar });
  }, []);

  const cerrarReunion = useCallback(async () => {
    const actual = reunionRef.current;
    if (!actual) return;
    setReunion(null);
    if (actual.alCerrar) { try { await actual.alCerrar(); } catch (_) {} }
  }, []);

  // Cerrar sesión (o sesión expirada) → la ventana no debe quedar abierta.
  useEffect(() => { if (!user) setReunion(null); }, [user]);

  return (
    <MeetingContext.Provider value={{ reunion, abrirReunion, cerrarReunion }}>
      {children}
      {reunion && (
        <ZoomFloat
          key={reunion.assemblyId}
          assemblyId={reunion.assemblyId}
          assemblyName={reunion.assemblyName}
          isModerator={reunion.isModerator}
          onClose={cerrarReunion}
        />
      )}
    </MeetingContext.Provider>
  );
}

export const useMeeting = () => useContext(MeetingContext);
